import { Bullet, Circle, Label, p100, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import { LegendData } from "@/classes/BaseChart";
import ColumnChart from "@/classes/ColumnChart";
import LineChart from "@/classes/LineChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import extendClass from "@/helpers/extendClass";
import { stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

class ComboColumnLineChart extends ColumnChart {
  public loadChart(chartName: string, family: string): void {
    super.loadChart(chartName, family);
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;
  }

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;
    }

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = this.isDateAxis ? [this.settings.yAxisPrimary] : this.data;

    const lineColorIndex = 1;

    series.forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = (index % paletteLength >= lineColorIndex ? index + 1 : index) % paletteLength;
      this.createColumnSeries(el, indexColor);
    });
    this.createLineSeries(this.settings.yAxisSecondary, lineColorIndex);

    const legendNames: string[] = this.isDateAxis
      ? [this.settings.yAxisPrimary, this.settings.yAxisSecondary]
      : [...(this.data.map((item) => item[this.settings.xAxis]) as string[]), this.settings.yAxisSecondary];

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData, false, false);
  }

  public createColumnSeries(item: ChartDataRecord, index: number) {
    const seriesConfig = {
      name: this.isDateAxis ? this.settings.yAxisPrimary : (item[this.settings.xAxis] as string),
      xAxis: this.axes.x,
      yAxis: this.axes.yPrimary,
      valueYField: this.settings.yAxisPrimary,
      [this.isDateAxis ? "valueXField" : "categoryXField"]: this.settings.xAxis,
      fill: this.getSeriesColor(index),
    };

    if (!this.isDateAxis) {
      (seriesConfig as Record<string, unknown>).clustered = false;
    }

    const series: ColumnSeries = (this.chart as XYChart).series.push(ColumnSeries.new(this.root, seriesConfig));

    if (this.settings.showBarDataLabels) {
      const metricFormatter = this.formatters?.[this.settings.yAxisPrimary];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -32,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          locationY: 1,
          sprite: label,
        });
      });
    }

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueYField") === key || series.get("categoryXField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    if (this.isDateAxis) {
      this.createLegendLabel(series, this.settings.yAxisPrimary);
      this.updateColumnSeriesData(this.settings.yAxisPrimary, series);
    } else {
      series.data.push(item);
    }
  }

  createAxes() {
    const x = this.isDateAxis
      ? (this.chart as XYChart).xAxes.push(
          DateAxis.new(this.root, {
            renderer: this.renderers.x as AxisRendererX,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.xAxis),
          })
        )
      : (this.chart as XYChart).xAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.xAxis,
            renderer: this.renderers.x as AxisRendererX,
            marginTop: 14,
          })
        );

    x.data.setAll(this.data);

    if (!this.isDateAxis) {
      const xRenderer = this.renderers.x as AxisRendererX;

      xRenderer.set("minGridDistance", 1);

      const getCategoryCellWidth = () => {
        const plotWidth = (this.chart as XYChart).plotContainer.getPrivate("width") ?? 0;
        const count = x?.dataItems?.length ?? 0;
        return count > 0 ? plotWidth / count : plotWidth;
      };

      const updateXAxisLabelsLayout = () => {
        const cell = getCategoryCellWidth();
        const padding = 16;
        const maxWidth = Math.max(30, Math.floor(cell - padding));

        const behavior = cell < 70 ? "truncate" : "wrap";

        xRenderer.labels.template.setAll({
          ...chartFontSettings.label,
          textAlign: "center",
          rotation: 0,
          centerX: p50,
          centerY: 0.5,
          maxWidth,
          oversizedBehavior: behavior,
          tooltipText: `{${this.settings.xAxis}}`,
          visible: true,
        });
      };

      (this.chart as XYChart).plotContainer.onPrivate("width", updateXAxisLabelsLayout);
      (this.chart as XYChart).plotContainer.onPrivate("height", updateXAxisLabelsLayout);

      if ("events" in x) {
        (x as CategoryAxis<AxisRendererX>).events.on("datavalidated", updateXAxisLabelsLayout);
      }

      updateXAxisLabelsLayout();
    }

    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          paddingBottom: 0,
          centerX: p50,
        })
      );
    }

    const yPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.yPrimary as AxisRendererY,
        min: 0,
        extraMax: this.settings.showBarDataLabels ? 0.1 : 0,
        numberFormat: this.getMetricFormatter(this.settings.yAxisPrimary, { notation: "compact" }),
      })
    );

    const ySecondary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.ySecondary as AxisRendererY,
        min: 0,
        extraMax: this.settings.showLineDataLabels ? 0.1 : 0,
        numberFormat: this.getMetricFormatter(this.settings.yAxisSecondary, { notation: "compact" }),
      })
    );

    const yAxisPrimaryTitle = this.settings.yAxisPrimaryTitle;
    if (yAxisPrimaryTitle) {
      yPrimary.children.unshift(
        Label.new(this.root, {
          text: yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    const yAxisSecondaryTitle = this.settings.yAxisSecondaryTitle;
    if (yAxisSecondaryTitle) {
      ySecondary.children.push(
        Label.new(this.root, {
          text: yAxisSecondaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: 90,
          x: p100,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, yPrimary, ySecondary };
  }

  protected createLineSeries(seriesName: string, index: number) {
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series: LineSeries = (this.chart as XYChart).series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.x,
        yAxis: this.axes.ySecondary,
        fill: this.getSeriesColor(index),
        minBulletDistance: 10,
        valueYField: seriesName,
        [this.isDateAxis ? "valueXField" : "categoryXField"]: this.settings.xAxis,
      })
    );

    if (this.options.showBullets) {
      series.bullets.push(() => {
        return Bullet.new(this.root, {
          sprite: Circle.new(this.root, {
            radius: 4,
            fill: series.get("fill"),
          }),
        });
      });
    }

    if (this.settings.showLineDataLabels) {
      const metricFormatter = this.formatters?.[seriesName];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsLinesSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -24,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          locationY: 1,
          sprite: label,
        });
      });
    }

    this.createTooltip(series, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueYField") === key || series.get("categoryXField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    series.set("stroke", this.getSeriesColor(index));
    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    this.createLegendLabel(series, seriesName);
    this.updateLineSeriesData(seriesName, series);
  }

  protected updateLineSeriesData(seriesName: string, series: LineSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.xAxis]: stringOrNumberToDate(el[this.settings.xAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data;

    data.sort((a, b) => {
      return (a[this.settings.xAxis] as number) - (b[this.settings.xAxis] as number);
    });

    series.data.setAll(data);
  }

  mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      (this.module === PLAN_MODULE || this.isDualMode) &&
      (!this.settings?.useExploreData || this.isDualMode) &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.xAxis;
      const displayMetric = this.settings.yAxisPrimary;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((item) => ({
      [this.settings.xAxis]: item[this.settings.xAxis] ?? Lang.null,
      [this.settings.yAxisPrimary]: item[this.settings.yAxisPrimary] ?? 0,
      [this.settings.yAxisSecondary]: item[this.settings.yAxisSecondary] ?? 0,
    }));

    if (!this.isDateAxis) {
      preparedData = orderBy(preparedData, [this.settings.yAxisPrimary], ["desc"]) as ChartDataRecord[];
      preparedData = this.showItems(preparedData, this.settings.xAxis, this.settings.yAxisPrimary);
    }

    return preparedData;
  }
}

extendClass(ComboColumnLineChart, LineChart, ["createRenderers"]);

export default ComboColumnLineChart;
